"use client";

/**
 * ServiceWorkerRegistration
 *
 * Registers /sw.js and provides:
 *   1. Offline caching for schedule, appointments, patient records
 *   2. Mutation queue for SOAP notes and check-ins when offline
 *   3. Auto-sync when connection is restored
 *   4. "Offline Mode" indicator bar + pending queue count
 *
 * Mount once in the root layout.tsx.
 */

import { useEffect, useState, useCallback } from "react";
import { WifiOff, RefreshCw, CheckCircle, Loader2 } from "lucide-react";

type SyncState = "idle" | "syncing" | "done";

export default function ServiceWorkerRegistration() {
  const [isOffline, setIsOffline] = useState(false);
  const [queueSize, setQueueSize] = useState(0);
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [syncedCount, setSyncedCount] = useState(0);

  // Ask the SW for the current queue size
  const refreshQueueSize = useCallback(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: "GET_QUEUE_SIZE" });
  }, []);

  // Trigger the SW to replay the offline queue immediately
  const triggerSync = useCallback(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: "TRIGGER_SYNC" });
    setSyncState("syncing");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Set initial online/offline state
    setIsOffline(!navigator.onLine);

    // Online/Offline events
    function handleOnline() {
      setIsOffline(false);
      // Automatically replay queued mutations when we come back online
      triggerSync();
      // Also dispatch the legacy custom event for backwards compat
      window.dispatchEvent(new CustomEvent("sw-flush-offline-queue"));
    }

    function handleOffline() {
      setIsOffline(true);
      setSyncState("idle");
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [triggerSync]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    // Only register in production or when explicitly enabled in dev
    const shouldRegister =
      process.env.NODE_ENV === "production" ||
      process.env.NEXT_PUBLIC_SW_ENABLED === "true";

    if (!shouldRegister) return;

    let registration: ServiceWorkerRegistration | null = null;

    async function register() {
      try {
        registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
          updateViaCache: "none",
        });

        console.log("[SW] Registered:", registration.scope);

        // Check for updates every 60 seconds when page is visible
        const updateInterval = setInterval(() => {
          if (!document.hidden && registration) {
            registration.update().catch(() => {});
          }
        }, 60 * 1000);

        // Handle new service worker waiting to activate
        registration.addEventListener("updatefound", () => {
          const newWorker = registration?.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              console.info("[SW] New version available. Refresh to update.");
              window.dispatchEvent(
                new CustomEvent("sw-update-available", { detail: { registration } })
              );
            }
          });
        });

        // Once registered, ask for initial queue size
        refreshQueueSize();

        return () => clearInterval(updateInterval);
      } catch (err) {
        console.warn("[SW] Registration failed:", err);
      }
    }

    if (document.readyState === "complete") {
      register();
    } else {
      window.addEventListener("load", register, { once: true });
    }

    // Handle messages from the SW
    function handleMessage(event: MessageEvent) {
      const { data } = event;
      if (!data?.type) return;

      switch (data.type) {
        case "FLUSH_OFFLINE_QUEUE":
          // Legacy — delegate to the offline queue module
          window.dispatchEvent(new CustomEvent("sw-flush-offline-queue"));
          break;

        case "MUTATION_QUEUED":
          // A mutation was queued because network was unavailable
          setQueueSize((n) => n + 1);
          break;

        case "SYNC_STARTED":
          setSyncState("syncing");
          break;

        case "SYNC_COMPLETE":
          setSyncState("done");
          setSyncedCount(data.synced || 0);
          setQueueSize(data.failed || 0);
          // Reset done indicator after 4 seconds
          setTimeout(() => setSyncState("idle"), 4000);
          break;

        case "QUEUE_SIZE":
          setQueueSize(data.size || 0);
          break;
      }
    }

    navigator.serviceWorker.addEventListener("message", handleMessage);

    // Periodically refresh queue size
    const queuePoll = setInterval(refreshQueueSize, 15000);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
      clearInterval(queuePoll);
    };
  }, [refreshQueueSize]);

  // ── Offline indicator bar ────────────────────────────────────────────────
  // Show bar when: offline, or there are pending queued mutations
  const showBar = isOffline || queueSize > 0 || syncState !== "idle";

  if (!showBar) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium transition-colors ${
        isOffline
          ? "bg-amber-500 text-white"
          : syncState === "syncing"
          ? "bg-blue-500 text-white"
          : syncState === "done"
          ? "bg-emerald-600 text-white"
          : "bg-amber-400 text-amber-900"
      }`}
    >
      {isOffline && (
        <>
          <WifiOff size={13} />
          <span>Offline Mode</span>
          {queueSize > 0 && (
            <span className="bg-white/20 rounded-full px-2 py-0.5">
              {queueSize} change{queueSize !== 1 ? "s" : ""} pending sync
            </span>
          )}
          {queueSize === 0 && (
            <span className="opacity-80">Schedule and patient data available from cache</span>
          )}
        </>
      )}
      {!isOffline && syncState === "syncing" && (
        <>
          <Loader2 size={13} className="animate-spin" />
          <span>Syncing offline changes…</span>
        </>
      )}
      {!isOffline && syncState === "done" && (
        <>
          <CheckCircle size={13} />
          <span>
            {syncedCount} change{syncedCount !== 1 ? "s" : ""} synced successfully
          </span>
        </>
      )}
      {!isOffline && syncState === "idle" && queueSize > 0 && (
        <>
          <WifiOff size={13} />
          <span>{queueSize} change{queueSize !== 1 ? "s" : ""} waiting to sync</span>
          <button
            onClick={triggerSync}
            className="flex items-center gap-1 underline hover:no-underline"
          >
            <RefreshCw size={11} />
            Sync now
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Helper: invalidate a specific API cache pattern from client code.
 * Call after mutations to ensure stale data is cleared.
 *
 * Example: invalidateSwCache('/api/schedule')
 */
export function invalidateSwCache(pattern?: string) {
  if (!("serviceWorker" in navigator)) return;
  if (!navigator.serviceWorker.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: "INVALIDATE_CACHE",
    pattern,
  });
}
